import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardActivity } from '../../../store/reducer/DashboardReducer';
import { formatDateTime } from '../../../utils/functions';
import Button from '../../shared/TailwindComponents/Button';
import {
  PiArrowRightLight,
  PiClockCounterClockwiseLight,
  PiPlusCircleLight,
  PiPencilSimpleLight,
  PiTrashLight,
  PiCheckCircleLight,
  PiLinkLight,
  PiLinkBreakLight,
  PiUserPlusLight,
  PiUserMinusLight,
  PiShieldCheckLight,
} from 'react-icons/pi';

interface RecentActivityFeedProps {
  activities: DashboardActivity[];
}

interface ActionConfig {
  icon: React.ReactElement;
  color: string;
  bg: string;
}

const ACTION_CONFIG: Record<string, ActionConfig> = {
  created:              { icon: <PiPlusCircleLight />,   color: 'text-green-600 dark:text-green-400',     bg: 'bg-green-50 dark:bg-green-900/30' },
  create:               { icon: <PiPlusCircleLight />,   color: 'text-green-600 dark:text-green-400',     bg: 'bg-green-50 dark:bg-green-900/30' },
  updated:              { icon: <PiPencilSimpleLight />, color: 'text-blue-500 dark:text-blue-400',       bg: 'bg-blue-50 dark:bg-blue-900/30' },
  update:               { icon: <PiPencilSimpleLight />, color: 'text-blue-500 dark:text-blue-400',       bg: 'bg-blue-50 dark:bg-blue-900/30' },
  deleted:              { icon: <PiTrashLight />,        color: 'text-red-500 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-900/30' },
  delete:               { icon: <PiTrashLight />,        color: 'text-red-500 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-900/30' },
  link:                 { icon: <PiLinkLight />,         color: 'text-teal-600 dark:text-teal-400',       bg: 'bg-teal-50 dark:bg-teal-900/30' },
  linked:               { icon: <PiLinkLight />,         color: 'text-teal-600 dark:text-teal-400',       bg: 'bg-teal-50 dark:bg-teal-900/30' },
  unlink:               { icon: <PiLinkBreakLight />,    color: 'text-amber-500 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/30' },
  unlinked:             { icon: <PiLinkBreakLight />,    color: 'text-amber-500 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/30' },
  collaborator_added:   { icon: <PiUserPlusLight />,     color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30' },
  collaborator_add:     { icon: <PiUserPlusLight />,     color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30' },
  collaborator_removed: { icon: <PiUserMinusLight />,    color: 'text-amber-500 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/30' },
  collaborator_remove:  { icon: <PiUserMinusLight />,    color: 'text-amber-500 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/30' },
  grant_access:         { icon: <PiShieldCheckLight />,  color: 'text-sky-600 dark:text-sky-400',         bg: 'bg-sky-50 dark:bg-sky-900/30' },
  completed:            { icon: <PiCheckCircleLight />,  color: 'text-green-600 dark:text-green-400',     bg: 'bg-green-50 dark:bg-green-900/30' },
  milestone_completed:  { icon: <PiCheckCircleLight />,  color: 'text-green-600 dark:text-green-400',     bg: 'bg-green-50 dark:bg-green-900/30' },
};

const DEFAULT_CONFIG: ActionConfig = {
  icon: <PiClockCounterClockwiseLight />,
  color: 'text-slate-500 dark:text-slate-400',
  bg: 'bg-slate-50 dark:bg-slate-800/50',
};

const formatTimeAgo = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const diffDays = Math.floor(diffSec / 86400);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTime(dateStr);
};

const capitalize = (str: string): string =>
  str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatActivityMessage = (activity: DashboardActivity): string => {
  if (activity.details) return activity.details;
  const entity = capitalize(activity.entity_type || 'Item');
  const action = capitalize(activity.action || 'modified');
  return `${entity} ${action}`;
};

const RecentActivityFeed: React.FC<RecentActivityFeedProps> = ({ activities }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const capped = activities.slice(0, 12);
  if (capped.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-800 dark:text-gray-300" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('Recent Activity')}
          </h3>
        </div>
        <Button variant="outline" size="small" onClick={() => history.push('/account?tab=activity')}>
          {t('View All')}
        </Button>
      </div>

      {/* Connected timeline */}
      {/* Vertical line centred under icons: px-4 (16px) + w-8/2 (16px) = 32px = left-8 */}
      <div className="relative px-4 py-2">
        <div className="absolute left-8 top-4 bottom-4 w-px bg-gray-100 dark:bg-gray-700/70 pointer-events-none" />

        {capped.map((activity) => {
          const config = ACTION_CONFIG[activity.action] || DEFAULT_CONFIG;
          return (
            <div
              key={activity.id}
              className="relative flex items-start gap-3 py-2 cursor-pointer group"
              onClick={() => { if (activity.link) history.push(activity.link); }}
            >
              {/* Icon circle - ring cuts through the vertical line for clean look */}
              <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${config.bg} ring-2 ring-white dark:ring-gray-800`}>
                <span className={`text-sm ${config.color}`}>{config.icon}</span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-1 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  {formatActivityMessage(activity)}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {activity.entity_type && (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {activity.entity_type.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                    {formatTimeAgo(activity.created_at)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivityFeed;
